/**
 * Cloud Billing Sync — admin UI for Azure & GCP production ingest
 * =================================================================
 * Wraps /api/cloud-billing-sync/{azure,gcp,status}.
 * Lives at /admin/cloud-billing-sync.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SEOHead from '../../components/SEOHead';
import {
  ArrowLeft, Cloud, Loader2, CheckCircle2, AlertTriangle,
  RefreshCw, ExternalLink, KeyRound, Save, Trash2, Vault, Zap,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CloudBillingSync = () => {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  // Azure form
  const [azure, setAzure] = useState({
    tenant_id: '', client_id: '', client_secret: '', subscription_id: '', days: 30,
  });
  const [azureBusy, setAzureBusy] = useState(false);
  const [azureResult, setAzureResult] = useState(null);
  // GCP form
  const [gcp, setGcp] = useState({
    sa_json: '', bq_project_id: '', bq_dataset: 'billing_export', bq_table: '', days: 30,
  });
  const [gcpBusy, setGcpBusy] = useState(false);
  const [gcpResult, setGcpResult] = useState(null);

  // Vault state
  const [vaultItems, setVaultItems] = useState([]);
  const [vaultLoading, setVaultLoading] = useState(true);
  const [saveAzureAs, setSaveAzureAs] = useState('');
  const [saveGcpAs, setSaveGcpAs] = useState('');
  const [selectedAzureCred, setSelectedAzureCred] = useState('');
  const [selectedGcpCred, setSelectedGcpCred] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/cloud-billing-sync/status`, { headers });
      setStatus(data);
    } catch (e) {
      setStatus({ clouds: {} });
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); fetchVault(); /* eslint-disable-next-line */ }, []);

  const fetchVault = async () => {
    setVaultLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/credential-vault/list`, { headers });
      setVaultItems(data.items || []);
    } catch (e) {
      setVaultItems([]);
    } finally {
      setVaultLoading(false);
    }
  };

  const saveAzureCred = async () => {
    if (!saveAzureAs) { alert('Enter a credential name first.'); return; }
    if (!azure.tenant_id || !azure.client_id || !azure.client_secret || !azure.subscription_id) {
      alert('Fill all 4 Azure fields before saving.'); return;
    }
    try {
      await axios.post(`${API_URL}/credential-vault/save`, {
        name: saveAzureAs, cloud: 'azure', azure: {
          tenant_id: azure.tenant_id, client_id: azure.client_id,
          client_secret: azure.client_secret, subscription_id: azure.subscription_id,
        },
      }, { headers });
      setSaveAzureAs('');
      await fetchVault();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not save');
    }
  };

  const saveGcpCred = async () => {
    if (!saveGcpAs) { alert('Enter a credential name first.'); return; }
    let parsed;
    try { parsed = JSON.parse(gcp.sa_json); } catch (e) {
      alert('Service-account JSON is not valid JSON'); return;
    }
    try {
      await axios.post(`${API_URL}/credential-vault/save`, {
        name: saveGcpAs, cloud: 'gcp', gcp: {
          service_account: parsed,
          bq_project_id: gcp.bq_project_id,
          bq_dataset: gcp.bq_dataset,
          bq_table: gcp.bq_table,
        },
      }, { headers });
      setSaveGcpAs('');
      await fetchVault();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not save');
    }
  };

  const deleteCred = async (name) => {
    if (!window.confirm(`Delete saved credential "${name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/credential-vault/${name}`, { headers });
      await fetchVault();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const runAzureByName = async () => {
    if (!selectedAzureCred) return;
    setAzureBusy(true);
    setAzureResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/cloud-billing-sync/azure-by-name`, {
        name: selectedAzureCred, days: azure.days,
      }, { headers });
      setAzureResult(data);
      await fetchStatus();
    } catch (e) {
      setAzureResult({ ok: false, detail: e?.response?.data?.detail || 'Failed' });
    } finally {
      setAzureBusy(false);
    }
  };

  const runGcpByName = async () => {
    if (!selectedGcpCred) return;
    setGcpBusy(true);
    setGcpResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/cloud-billing-sync/gcp-by-name`, {
        name: selectedGcpCred, days: gcp.days,
      }, { headers });
      setGcpResult(data);
      await fetchStatus();
    } catch (e) {
      setGcpResult({ ok: false, detail: e?.response?.data?.detail || 'Failed' });
    } finally {
      setGcpBusy(false);
    }
  };

  const syncAzure = async () => {
    setAzureBusy(true);
    setAzureResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/cloud-billing-sync/azure`, azure, { headers });
      setAzureResult(data);
      await fetchStatus();
    } catch (e) {
      setAzureResult({ ok: false, detail: e?.response?.data?.detail || e?.message || 'Failed' });
    } finally {
      setAzureBusy(false);
    }
  };

  const syncGcp = async () => {
    setGcpBusy(true);
    setGcpResult(null);
    let parsed;
    try {
      parsed = JSON.parse(gcp.sa_json);
    } catch (e) {
      setGcpResult({ ok: false, detail: 'Service-account JSON is not valid JSON' });
      setGcpBusy(false);
      return;
    }
    const required = ['type', 'project_id', 'private_key_id', 'private_key', 'client_email', 'client_id'];
    for (const k of required) {
      if (!parsed[k]) {
        setGcpResult({ ok: false, detail: `Service-account JSON missing field: ${k}` });
        setGcpBusy(false);
        return;
      }
    }
    try {
      const { data } = await axios.post(`${API_URL}/cloud-billing-sync/gcp`, {
        service_account: parsed,
        bq_project_id: gcp.bq_project_id,
        bq_dataset: gcp.bq_dataset,
        bq_table: gcp.bq_table,
        days: gcp.days,
      }, { headers });
      setGcpResult(data);
      await fetchStatus();
    } catch (e) {
      setGcpResult({ ok: false, detail: e?.response?.data?.detail || e?.message || 'Failed' });
    } finally {
      setGcpBusy(false);
    }
  };

  const renderResult = (r) => {
    if (!r) return null;
    const ok = r.ok;
    return (
      <div className={`mt-3 text-xs p-2.5 rounded border ${ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100' : 'bg-rose-500/10 border-rose-500/30 text-rose-200'}`}>
        {ok ? (
          <>
            <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
            Synced <b className="tabular-nums">{r.points_ingested}</b> points across <b className="tabular-nums">{r.days_covered}</b> days
            {r.duration_ms ? <> · {r.duration_ms}ms</> : null}.
          </>
        ) : (
          <><AlertTriangle className="w-4 h-4 inline mr-1.5" /> {r.detail || r.reason || 'Failed'}</>
        )}
      </div>
    );
  };

  const renderCloudStatus = (key, label) => {
    if (statusLoading) return <span className="text-slate-600">Loading…</span>;
    const s = status?.clouds?.[key];
    if (!s) return <span className="text-slate-600">never synced</span>;
    const date = (s.last_sync_at || '').split('T')[0];
    return (
      <span className={s.last_sync_ok ? 'text-emerald-300' : 'text-rose-300'}>
        {s.last_sync_ok ? '✓' : '✗'} {date} · {s.last_sync_points} points
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead title="Cloud Billing Sync · CARTIEAI" noindex />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/commitment-rebalancer" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="cbs-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Rebalancer
          </Link>
        </div>

        <div className="mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-200 mb-3">Admin · Production cloud sync</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="cbs-title">
            <Cloud className="w-7 h-7 inline-block mr-2 text-emerald-400" />
            Cloud billing sync — Azure + GCP
          </h1>
          <p className="text-sm text-slate-700">
            Live production ingest into the same <code>commitment_spend_curve</code> the rebalancer reads.
            Azure works with just OAuth credentials. GCP requires you to enable Billing Export → BigQuery first.
          </p>
          <div className="mt-3 text-xs text-slate-600 space-y-1" data-testid="cbs-status-summary">
            <div>Azure: {renderCloudStatus('azure', 'Azure')}</div>
            <div>GCP:   {renderCloudStatus('gcp', 'GCP')}</div>
          </div>
        </div>

        {/* Saved credentials vault */}
        <Card className="border-emerald-500/30 mb-6" data-testid="cbs-vault-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Vault className="w-5 h-5 text-emerald-400" /> Saved credentials
            </CardTitle>
            <CardDescription className="text-slate-400">
              Fernet-encrypted at rest. Use the dropdown on each card below to run a one-click sync — no more re-pasting secrets.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {vaultLoading ? (
              <div className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
            ) : vaultItems.length === 0 ? (
              <div className="text-xs text-slate-600" data-testid="cbs-vault-empty">No saved credentials yet. Fill the form below and click "Save these credentials" to persist for next time.</div>
            ) : (
              <ul className="space-y-2" data-testid="cbs-vault-list">
                {vaultItems.map((it) => (
                  <li key={`${it.cloud}-${it.name}`} className="flex items-center justify-between text-sm border border-emerald-500/20 rounded-md px-3 py-2 bg-slate-900" data-testid={`cbs-vault-row-${it.name}`}>
                    <div>
                      <Badge className={`mr-2 ${it.cloud === 'azure' ? 'bg-emerald-100 text-blue-800' : 'bg-amber-500/20 text-amber-200'}`}>{it.cloud.toUpperCase()}</Badge>
                      <span className="font-medium text-slate-900">{it.name}</span>
                      <span className="text-xs text-slate-600 ml-2">· {it.hint}</span>
                    </div>
                    <Button onClick={() => deleteCred(it.name)} size="sm" variant="outline" className="h-7 px-2 text-rose-300 border-rose-500/40" data-testid={`cbs-vault-delete-${it.name}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Azure card */}
        <Card className="border-emerald-200 mb-6" data-testid="cbs-azure-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-emerald-600">Azure</span> · Cost Management Query API
            </CardTitle>
            <CardDescription className="text-slate-400">
              Needs a service principal with <code>Cost Management Reader</code> on the subscription.
              Daily granularity, classified by service into compute/storage/egress/refunds.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Quick-sync from saved vault */}
            {vaultItems.filter(v => v.cloud === 'azure').length > 0 && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md" data-testid="cbs-azure-quicksync">
                <div className="text-xs font-medium text-emerald-100 mb-1.5 flex items-center gap-1"><Zap className="w-3 h-3" /> One-click from vault</div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedAzureCred}
                    onChange={(e) => setSelectedAzureCred(e.target.value)}
                    className="h-9 px-2 rounded border border-emerald-300 bg-slate-900 text-sm flex-1"
                    data-testid="cbs-azure-cred-select"
                  >
                    <option value="">Pick a saved credential…</option>
                    {vaultItems.filter(v => v.cloud === 'azure').map(v => (
                      <option key={v.name} value={v.name}>{v.name} · {v.hint}</option>
                    ))}
                  </select>
                  <Button onClick={runAzureByName} disabled={!selectedAzureCred || azureBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cbs-azure-run-saved-btn">
                    {azureBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-3 h-3 mr-1" /> Run sync</>}
                  </Button>
                </div>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Tenant ID (GUID)" value={azure.tenant_id} onChange={(e) => setAzure({ ...azure, tenant_id: e.target.value })} data-testid="cbs-azure-tenant" />
              <Input placeholder="Client ID (App registration GUID)" value={azure.client_id} onChange={(e) => setAzure({ ...azure, client_id: e.target.value })} data-testid="cbs-azure-client" />
              <Input type="password" placeholder="Client secret" value={azure.client_secret} onChange={(e) => setAzure({ ...azure, client_secret: e.target.value })} data-testid="cbs-azure-secret" />
              <Input placeholder="Subscription ID (GUID)" value={azure.subscription_id} onChange={(e) => setAzure({ ...azure, subscription_id: e.target.value })} data-testid="cbs-azure-subscription" />
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Days to sync</label>
                <Input type="number" min="7" max="90" value={azure.days} onChange={(e) => setAzure({ ...azure, days: Number(e.target.value) || 30 })} data-testid="cbs-azure-days" />
              </div>
            </div>
            <Button onClick={syncAzure} disabled={azureBusy || !azure.tenant_id || !azure.client_id || !azure.client_secret || !azure.subscription_id} className="mt-3 bg-blue-600 hover:bg-blue-700" data-testid="cbs-azure-sync-btn">
              {azureBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Syncing…</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Sync Azure</>}
            </Button>
            {/* Save-to-vault row */}
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="cbs-azure-save-row">
              <KeyRound className="w-3 h-3 text-slate-400" />
              <span className="text-xs text-slate-600">Save these for one-click reuse:</span>
              <Input
                type="text"
                value={saveAzureAs}
                onChange={(e) => setSaveAzureAs(e.target.value)}
                placeholder="prod-azure-readonly"
                className="h-8 w-48 text-xs"
                data-testid="cbs-azure-save-name"
              />
              <Button onClick={saveAzureCred} disabled={!saveAzureAs} size="sm" variant="outline" className="h-8 px-3" data-testid="cbs-azure-save-btn">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
            {renderResult(azureResult)}
            <div className="mt-3 text-[11px] text-slate-600">
              Creds are sent in-request and discarded server-side immediately after the API roundtrip. We never persist them.
            </div>
          </CardContent>
        </Card>

        {/* GCP card */}
        <Card className="border-amber-500/30 mb-6" data-testid="cbs-gcp-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-amber-400">GCP</span> · BigQuery Billing Export
            </CardTitle>
            <CardDescription className="text-slate-400">
              Requires you to <a href="https://cloud.google.com/billing/docs/how-to/export-data-bigquery" target="_blank" rel="noreferrer" className="text-emerald-300 underline">enable Billing Export → BigQuery</a> in your GCP console first (one-time toggle, ~24h to populate).
              Then grant the service account <code>BigQuery Data Viewer</code> + <code>BigQuery Job User</code> on the dataset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Quick-sync from saved vault */}
            {vaultItems.filter(v => v.cloud === 'gcp').length > 0 && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md" data-testid="cbs-gcp-quicksync">
                <div className="text-xs font-medium text-emerald-100 mb-1.5 flex items-center gap-1"><Zap className="w-3 h-3" /> One-click from vault</div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedGcpCred}
                    onChange={(e) => setSelectedGcpCred(e.target.value)}
                    className="h-9 px-2 rounded border border-emerald-300 bg-slate-900 text-sm flex-1"
                    data-testid="cbs-gcp-cred-select"
                  >
                    <option value="">Pick a saved credential…</option>
                    {vaultItems.filter(v => v.cloud === 'gcp').map(v => (
                      <option key={v.name} value={v.name}>{v.name} · {v.hint}</option>
                    ))}
                  </select>
                  <Button onClick={runGcpByName} disabled={!selectedGcpCred || gcpBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cbs-gcp-run-saved-btn">
                    {gcpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-3 h-3 mr-1" /> Run sync</>}
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Service-account JSON (paste full content)</label>
                <textarea
                  rows={5}
                  className="w-full text-xs font-mono p-2 rounded border border-emerald-500/30"
                  placeholder='{"type":"service_account","project_id":"...","private_key_id":"...","private_key":"-----BEGIN PRIVATE KEY-----\\n...","client_email":"...","client_id":"..."}'
                  value={gcp.sa_json}
                  onChange={(e) => setGcp({ ...gcp, sa_json: e.target.value })}
                  data-testid="cbs-gcp-sa"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Input placeholder="BQ project id" value={gcp.bq_project_id} onChange={(e) => setGcp({ ...gcp, bq_project_id: e.target.value })} data-testid="cbs-gcp-bq-project" />
                <Input placeholder="BQ dataset (e.g. billing_export)" value={gcp.bq_dataset} onChange={(e) => setGcp({ ...gcp, bq_dataset: e.target.value })} data-testid="cbs-gcp-bq-dataset" />
                <Input placeholder="BQ table (gcp_billing_export_v1_XXXX_XXXX_XXXX)" value={gcp.bq_table} onChange={(e) => setGcp({ ...gcp, bq_table: e.target.value })} data-testid="cbs-gcp-bq-table" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Days to sync</label>
                <Input type="number" min="7" max="90" value={gcp.days} onChange={(e) => setGcp({ ...gcp, days: Number(e.target.value) || 30 })} data-testid="cbs-gcp-days" />
              </div>
              <Button onClick={syncGcp} disabled={gcpBusy || !gcp.sa_json || !gcp.bq_project_id || !gcp.bq_dataset || !gcp.bq_table} className="bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/20" data-testid="cbs-gcp-sync-btn">
                {gcpBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Syncing…</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Sync GCP</>}
              </Button>
              {/* Save-to-vault row */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="cbs-gcp-save-row">
                <KeyRound className="w-3 h-3 text-slate-400" />
                <span className="text-xs text-slate-600">Save these for one-click reuse:</span>
                <Input
                  type="text"
                  value={saveGcpAs}
                  onChange={(e) => setSaveGcpAs(e.target.value)}
                  placeholder="prod-gcp-readonly"
                  className="h-8 w-48 text-xs"
                  data-testid="cbs-gcp-save-name"
                />
                <Button onClick={saveGcpCred} disabled={!saveGcpAs} size="sm" variant="outline" className="h-8 px-3" data-testid="cbs-gcp-save-btn">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
              </div>
              {renderResult(gcpResult)}
            </div>
            <div className="mt-3 text-[11px] text-slate-600 flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              First-time setup: <a href="https://cloud.google.com/billing/docs/how-to/export-data-bigquery" target="_blank" rel="noreferrer" className="text-emerald-300 underline">cloud.google.com/billing/docs/how-to/export-data-bigquery</a>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-500/20 bg-slate-900 text-slate-100">
          <CardContent className="pt-4 text-xs text-slate-400">
            Synced data lands in the same <code>commitment_spend_curve</code> table as AWS CUR uploads.
            Visit <Link to="/admin/cost-actuals" className="text-emerald-300 hover:underline">/admin/cost-actuals</Link> to inspect/edit
            and <Link to="/commitment-rebalancer" className="text-emerald-300 hover:underline">/commitment-rebalancer</Link> to see the chart.
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CloudBillingSync;
